import { Controller } from "@hotwired/stimulus"
import { debounce } from "underscore"

/***
 * StickyNavbarController provides a sticky navigation bar that shows/hides
 * based on scroll position. When the user scrolls past a specified threshold,
 * the navbar becomes visible with enhanced styling.
 *
 * The controller automatically calculates the scroll threshold based on a
 * header element's height, or can use a manually specified threshold value.
 * It includes debounced scroll handling to help optimize performance.
 *
 * @example <caption>Basic sticky navbar</caption>
 *
 * <div data-controller="sticky-navbar">
 *   <div class="header" data-sticky-navbar-target="header">Header content</div>
 *   <nav data-sticky-navbar-target="navbar">Navigation content</nav>
 * </div>
 *
 * @example <caption>Navbar with custom threshold</caption>
 *
 * <div data-controller="sticky-navbar" data-sticky-navbar-threshold-value="200">
 *   <nav data-sticky-navbar-target="navbar">Navigation content</nav>
 * </div>
 *
 * @example <caption>Navbar with custom CSS class</caption>
 *
 * <div data-controller="sticky-navbar" data-sticky-navbar-show-class-value="navbar-visible">
 *   <div class="header" data-sticky-navbar-target="header">Header content</div>
 *   <nav data-sticky-navbar-target="navbar">Navigation content</nav>
 * </div>
 *
 ***/
export default class extends Controller {
  static targets = ["navbar", "header"]
  static values = {
    threshold: { type: Number, default: 0 },
    showClass: { type: String, default: "show-nav" }
  }

  // Private

  connect() {
    this.calculateThreshold()
    this.debouncedShowOrHide = debounce(this.showOrHide.bind(this), 10)
    this.showOrHide()
    window.addEventListener('scroll', this.debouncedShowOrHide)
  }

  disconnect() {
    window.removeEventListener('scroll', this.debouncedShowOrHide)
  }

  calculateThreshold() {
    if (this.hasHeaderTarget) {
      const boundingClient = this.headerTarget.getBoundingClientRect()
      this.thresholdValue = boundingClient.height
    }
  }

  showOrHide() {
    if (window.scrollY >= this.thresholdValue) {
      this.navbarTarget.classList.add(this.showClassValue)
    } else {
      this.navbarTarget.classList.remove(this.showClassValue)
    }
  }
}
